import {
  formatCampaignCode,
  isStringOrNumber,
} from "@hooks/functions/Misc/Texts";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import { FiAlertTriangle, FiCheckSquare } from "react-icons/fi";
import { CgSpinner } from "react-icons/cg";

interface IProps {
  campaignName: string;
  onCampaignCodeChange: (
    campaignCode: string | undefined,
    isValidated: boolean
  ) => void;
}

interface ISlugAvailabilityDTO {
  can: boolean;
  slug: string;
  code: string;
  message: string;
}

export function RaffleCreateCampaignCodeByText({
  campaignName,
  onCampaignCodeChange,
}: IProps) {
  // --- Default Variables --- //
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Control States --- //
  const [isCampaignCodeValidated, setIsCampaignCodeValidated] =
    useState<boolean>(false);
  const [tmpCampaignCode, setTmpCampaignCode] = useState<string>("");
  const [campaignCode, setCampaignCode] = useState<string | undefined>(
    undefined
  );
  const [isValidatingCampaignCode, setIsValidatingCampaignCode] =
    useState<boolean>(false);

  const treatFormatCampaignCode = async (text: string) => {
    const validated = formatCampaignCode(text, 50);
    const newCampaignCode = validated
      ? validated
      : validated === ""
      ? validated
      : tmpCampaignCode;
    setTmpCampaignCode(newCampaignCode);
  };

  const handleValidateCampaignCode = async () => {
    // --- Check if have some Campaign Code --- //
    if (!tmpCampaignCode)
      return pushToastify({
        message: "É obrigatório informar um código de campanha.",
        type: "error",
        theme: "dark",
      });

    // --- Check if the First and Last character is Letter ou Number --- //
    const firstLetter = tmpCampaignCode[0];
    const lastLetter = tmpCampaignCode[tmpCampaignCode.length - 1];
    if (!isStringOrNumber(firstLetter) || !isStringOrNumber(lastLetter))
      return pushToastify({
        message:
          "O primeiro e o último caractere precisa ser um número ou letra.",
        type: "error",
        theme: "dark",
      });

    // --- Check on DB if this Customer can use this Campaign Code --- //
    const canUseCampaignCode = await canUseThisCampaignCode(tmpCampaignCode);
    if (!canUseCampaignCode)
      return;

    // --- Set the States values --- //
    setIsCampaignCodeValidated(true);
    setCampaignCode(tmpCampaignCode);
  };

  const canUseThisCampaignCode = async (campaignCode: string) => {
    // --- Check if have some Campaign Code --- //
    if (!campaignCode) {
      pushToastify({
        message:
          "O primeiro e o último caractere precisa ser um número ou letra.",
        type: "error",
        theme: "dark",
      });
      return false;
    }

    try {
      setIsValidatingCampaignCode(true);
      const params = `?${new URLSearchParams({
        slug: campaignCode,
      })}`;
      const response = await api.get<ISlugAvailabilityDTO>(
        `/customer/raffle/default/slug/availability${params}`,
      );

      if (!response.data.can) {
        pushToastify({
          message:
            response.data.message ||
            "Este código de campanha não está disponível.",
          type: "warning",
          theme: "dark",
        });
        return false;
      }

      pushToastify({
        message:
          response.data.message ||
          "Código de campanha disponível para utilização.",
        type: "success",
        theme: "dark",
      });
      return true;
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Falha ao carregar as informações.",
        type: "error",
        theme: "dark",
      });
      return false;
    } finally {
      setIsValidatingCampaignCode(false);
    }
  };

  const resetCampaignCodeValidation = () => {
    setIsCampaignCodeValidated(false);
    setCampaignCode(undefined);
  };

  useEffect(() => {
    if (campaignName) {
      treatFormatCampaignCode(campaignName);
    }
  }, [campaignName]);

  useEffect(() => {
    if (campaignCode && tmpCampaignCode != campaignCode) {
      resetCampaignCodeValidation();
    }
  }, [tmpCampaignCode]);

  useEffect(() => {
    // --- Send to Main Component --- //
    onCampaignCodeChange(campaignCode, isCampaignCodeValidated);
  }, [campaignCode, isCampaignCodeValidated]);

  return (
    <>
      {/* Creating the Campaign Code - Writing */}
      <div className="">
        <div className="d-flex justify-content-between">
          <label htmlFor="tmpCampaignCode">Código da Campanha</label>
          <span
            className={`text-${
              isCampaignCodeValidated ? `success` : `warning`
            } me-1`}
          >
            {`${
              isCampaignCodeValidated
                ? `Validado`
                : `Clique abaixo para validar`
            }`}
          </span>
        </div>
        <div className="input-group">
          <input
            id="tmpCampaignCode"
            type="text"
            maxLength={50}
            className={`form-control`}
            onChange={(e) => treatFormatCampaignCode(e.target.value)}
            value={tmpCampaignCode}
          />
          <button
            className="border-0 px-4"
            onClick={handleValidateCampaignCode}
            disabled={isValidatingCampaignCode}
          >
            {isValidatingCampaignCode ? (
              <CgSpinner className="loading-icon" />
            ) : isCampaignCodeValidated ? (
              <FiCheckSquare className="text-success fs-5" />
            ) : (
              <FiAlertTriangle className={`text-warning fs-5`} />
            )}
          </button>
        </div>
        <div className="d-flex flex-column">
          <small>
            Digite um código único para a sua campanha, esta será a url da
            mesma.
          </small>
          <small>
            * Permitido apenas: a-z, 0-9, hífens e underlines. (Ex:
            iphone-15-pro)
          </small>
          <small>
            * * É obrigatório consultar a disponibilidade do código antes de
            prosseguir.
          </small>
        </div>
      </div>

      {/* Loading the Campaign Code - Load from DB */}
      <div className=""></div>
    </>
  );
}
